import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDirectorDashboard } from '../hook/useDirectorDashboard.mjs';
import { getReportStatus, getStageLabel } from '../../utils/statusConfig.mjs';
import PageShell from '../../components/PageShell.mjs';

const GlobalDirectorDashboard = () => {
  const navigate = useNavigate();
  const {
    pendingReports,
    approvedReports,
    sentBackReports,
    loading,
    error,
    stats,
    user,
    fetchPendingReports,
    setError
  } = useDirectorDashboard('global');

  const [filterRegion, setFilterRegion] = useState('all');
  const [activeTab, setActiveTab] = useState('pending'); // 'pending' or 'approved'

  const formatCurrency = (amount) => {
    // parseFloat guard: amounts arrive as DECIMAL strings, which render as NaN.
    const n = parseFloat(amount);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(Number.isFinite(n) ? n : 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatPeriod = (month, year) => {
    const monthName = new Date(0, month - 1).toLocaleString('default', { month: 'long' });
    return `${monthName} ${year}`;
  };

  const handleViewReport = (reportId) => {
    navigate(`/monthly-report/${reportId}`);
  };

  // Get reports based on active tab
  const currentReports = activeTab === 'pending'
    ? pendingReports
    : activeTab === 'sentBack' ? sentBackReports : approvedReports;

  // Get unique regions for filter
  const regions = [...new Set(currentReports.map(r => r.region).filter(Boolean))];

  // Filter reports by region
  const filteredReports = filterRegion === 'all'
    ? currentReports
    : currentReports.filter(r => r.region === filterRegion);

  const shellProps = {
    // The global and finance director dashboards are now the pages of cards
    // that lead here.
    title: 'Reports Approval',
    subtitle: (
      <span className="inline-flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" aria-hidden="true"></span>
        Final approval for all regional reports
      </span>
    ),
  };

  if (loading) {
    return (
      <PageShell {...shellProps}>
        <div className="bg-white rounded-xl shadow-lg py-16 flex flex-col items-center justify-center">
          <div className="w-10 h-10 border-4 border-gray-200 border-t-stanford-red rounded-full animate-spin mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell {...shellProps}>
        {/* Error Display */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-2xl shadow-lg flex items-center justify-between">
            <span className="flex items-center gap-3">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span className="font-medium">{error}</span>
            </span>
            <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700 font-medium">
              Dismiss
            </button>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-4 sm:mb-6">
          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 min-w-0">
            <div className="shrink-0 w-10 h-10 sm:w-14 sm:h-14 rounded-full bg-blue-100 flex items-center justify-center">
              <svg className="w-5 h-5 sm:w-7 sm:h-7 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <div>
              <div className="text-xl sm:text-3xl font-bold tabular-nums break-words text-gray-800">{stats.totalPending}</div>
              <div className="text-sm text-gray-500">Pending Final Approval</div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 min-w-0">
            <div className="shrink-0 w-10 h-10 sm:w-14 sm:h-14 rounded-full bg-green-100 flex items-center justify-center">
              <svg className="w-5 h-5 sm:w-7 sm:h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <div className="text-xl sm:text-3xl font-bold tabular-nums break-words text-gray-800">{formatCurrency(stats.totalAmount)}</div>
              <div className="text-sm text-gray-500">Total Amount</div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 min-w-0">
            <div className="shrink-0 w-10 h-10 sm:w-14 sm:h-14 rounded-full bg-purple-100 flex items-center justify-center">
              <svg className="w-5 h-5 sm:w-7 sm:h-7 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <div className="text-xl sm:text-3xl font-bold tabular-nums break-words text-gray-800">{Object.keys(stats.byRegion).length}</div>
              <div className="text-sm text-gray-500">Regions with Pending</div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 min-w-0">
            <div className="shrink-0 w-10 h-10 sm:w-14 sm:h-14 rounded-full bg-emerald-100 flex items-center justify-center">
              <svg className="w-5 h-5 sm:w-7 sm:h-7 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <div className="text-xl sm:text-3xl font-bold tabular-nums break-words text-emerald-600">{stats.totalApproved}</div>
              <div className="text-sm text-gray-500">Approved Reports</div>
            </div>
          </div>
        </div>

        {/* Region breakdown */}
        {Object.keys(stats.byRegion).length > 0 && (
          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-stanford-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
              </svg>
              Reports by Region
            </h3>
            <div className="flex flex-wrap gap-3">
              {Object.entries(stats.byRegion).map(([region, count]) => (
                <button
                  key={region}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    filterRegion === region
                      ? 'bg-stanford-red text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  onClick={() => setFilterRegion(filterRegion === region ? 'all' : region)}
                >
                  {region}: {count}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Filter and Refresh */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4 sm:mb-6 bg-white rounded-xl shadow-lg px-4 sm:px-6 py-4">
          <div className="flex items-center gap-3 sm:gap-4">
            <label className="text-sm font-medium text-gray-600 whitespace-nowrap">Filter by Region:</label>
            <select
              value={filterRegion}
              onChange={(e) => setFilterRegion(e.target.value)}
              className="flex-1 sm:flex-none min-h-[44px] sm:min-h-0 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stanford-red focus:border-transparent"
            >
              <option value="all">All Regions</option>
              {regions.map(region => (
                <option key={region} value={region}>{region}</option>
              ))}
            </select>
          </div>
          <button
            onClick={fetchPendingReports}
            className="min-h-[44px] sm:min-h-0 justify-center px-4 py-2 bg-stanford-red text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>

        {/* Tabs */}
        <div className="grid grid-cols-3 sm:flex gap-2 mb-4 sm:mb-6">
          <button
            onClick={() => { setActiveTab('pending'); setFilterRegion('all'); }}
            className={`min-h-[44px] px-2 sm:px-6 py-2 sm:py-3 rounded-lg text-sm sm:text-base font-medium leading-tight transition-all ${
              activeTab === 'pending'
                ? 'bg-stanford-red text-white shadow-lg'
                : 'bg-white text-gray-600 hover:bg-gray-100 shadow'
            }`}
          >
            Pending Approval ({stats.totalPending})
          </button>
          <button
            onClick={() => { setActiveTab('approved'); setFilterRegion('all'); }}
            className={`min-h-[44px] px-2 sm:px-6 py-2 sm:py-3 rounded-lg text-sm sm:text-base font-medium leading-tight transition-all ${
              activeTab === 'approved'
                ? 'bg-green-600 text-white shadow-lg'
                : 'bg-white text-gray-600 hover:bg-gray-100 shadow'
            }`}
          >
            Approved ({stats.totalApproved})
          </button>
          <button
            onClick={() => { setActiveTab('sentBack'); setFilterRegion('all'); }}
            className={`min-h-[44px] px-2 sm:px-6 py-2 sm:py-3 rounded-lg text-sm sm:text-base font-medium leading-tight transition-all ${
              activeTab === 'sentBack'
                ? 'bg-amber-600 text-white shadow-lg'
                : 'bg-white text-gray-600 hover:bg-gray-100 shadow'
            }`}
          >
            Sent Back ({stats.totalSentBack || 0})
          </button>
        </div>

        {/* Reports Table */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full responsive-table">
              <thead className="bg-gray-800 text-white">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">Region</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">Period</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">Prepared By</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">Global Approved</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">Version</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredReports.map((report) => (
                  <tr key={report._id || report.id} className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 font-semibold text-gray-900" data-cell="title">{report.region || 'N/A'}</td>
                    <td className="px-6 py-4 text-gray-700" data-label="Period">
                      {report.month && report.year ? formatPeriod(report.month, report.year) : 'N/A'}
                    </td>
                    <td className="px-6 py-4 font-bold text-gray-900 tabular-nums" data-label="Amount">
                      {formatCurrency(report.totalAmount)}
                    </td>
                    <td className="px-6 py-4 text-gray-700" data-label="Prepared by">
                      {report.preparer
                        ? `${report.preparer.firstName} ${report.preparer.lastName}`
                        : '-'}
                    </td>
                    <td className="px-6 py-4 text-gray-600" data-label="Regional approval">
                      {formatDate(report.regionalDirectorApprovedAt)}
                    </td>
                    <td className="px-6 py-4" data-label="Version">
                      <span className={`px-3 py-1 rounded-lg text-xs font-medium ${
                        report.version > 1 ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-700'
                      }`}>
                        v{report.version || 1}
                      </span>
                    </td>
                    <td className="px-6 py-4" data-cell="action">
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          onClick={() => handleViewReport(report._id || report.id)}
                          className="px-3 py-2 text-sm text-stanford-red border border-stanford-red rounded-lg hover:bg-stanford-red hover:text-white transition-colors"
                        >
                          View
                        </button>
                        {/* The report's actual status, from the shared
                            vocabulary - which says "Awaiting Your Approval"
                            when this director is the one being waited on.
                            Previously only the fully-approved case rendered
                            anything, so every other row's status cell was
                            blank. Approving and sending back happen inside the
                            report. */}
                        <span className={`px-3 py-1.5 text-sm rounded-lg font-medium ${
                          getReportStatus(report.status, report, {
                            viewerRole: user?.role, viewerId: user?.id,
                          }).badge
                        }`}>
                          {getReportStatus(report.status, report, {
                            viewerRole: user?.role, viewerId: user?.id,
                          }).label}
                        </span>
                        {report.status === 'rejected' && (
                          <div className="basis-full text-xs text-amber-800 max-w-xs">
                            {getStageLabel(report.rejectionStage)}
                            {report.rejectionReason ? ` · ${report.rejectionReason}` : ''}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredReports.length === 0 && !loading && (
            <div className="text-center py-16">
              <div className={`w-16 h-16 mx-auto mb-4 rounded-full ${activeTab === 'pending' ? 'bg-green-100' : 'bg-gray-100'} flex items-center justify-center`}>
                <svg className={`w-8 h-8 ${activeTab === 'pending' ? 'text-green-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                {activeTab === 'pending' ? 'All Caught Up'
                  : activeTab === 'sentBack' ? 'Nothing Sent Back' : 'No Approved Reports'}
              </h3>
              <p className="text-gray-500">
                {activeTab === 'sentBack'
                  ? 'No reports have been sent back at the final stage.'
                  : activeTab === 'pending'
                  ? (filterRegion === 'all' ? 'No reports pending final approval.' : `No reports pending for ${filterRegion}.`)
                  : (filterRegion === 'all' ? 'No approved reports to display.' : `No approved reports for ${filterRegion}.`)}
              </p>
            </div>
          )}
        </div>
    </PageShell>
  );
};

export default GlobalDirectorDashboard;
